import { createFileRoute } from "@tanstack/react-router";

import { useI18n } from "~/lib/i18n";
import { installMessages } from "~/lib/i18n-install";
import { useInstall } from "~/lib/install";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/app")({
  component: InstallApp,
});

type Words = (typeof installMessages)["en"];

const benefits: (keyof Words)[] = ["whatIcon", "whatOffline", "whatUpdates", "whatSame", "whatPermissions"];

const success = "text-sm font-medium text-emerald-700 dark:text-emerald-400";
const list = "mt-1 space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300";

function InstallApp() {
  const { t, lang } = useI18n();
  const w = installMessages[lang];

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className={ui.h1}>{t("installApp")}</h1>
        <p className={ui.lead}>{w.intro}</p>
      </div>
      <section className={`${ui.card} flex items-start gap-4`}>
        <img src="/icons/icon-192.png" alt="" width={56} height={56} className="shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="leading-tight">
            <p lang="ne" className="font-semibold">
              {t("siteNameNe")}
            </p>
            <p lang="en" className={ui.muted}>
              {t("siteNameEn")}
            </p>
          </div>
          <InstallAction />
        </div>
      </section>
      <section>
        <h2 className={ui.h2}>{w.whatTitle}</h2>
        <ul className="mt-2 list-disc space-y-1.5 pl-5 text-slate-700 dark:text-slate-300">
          {benefits.map((b) => (
            <li key={b}>{w[b]}</li>
          ))}
        </ul>
      </section>
      <p className={ui.muted}>{w.remove}</p>
    </div>
  );
}

function InstallAction() {
  const w = installMessages[useI18n().lang];
  const { state, install } = useInstall();

  switch (state) {
    case "ready":
      return (
        <div className="space-y-2">
          <p className={ui.muted}>{w.ready}</p>
          <button type="button" onClick={() => void install()} className={ui.primary}>
            {w.button}
          </button>
        </div>
      );
    case "done":
      return (
        <p role="status" className={success}>
          {w.done}
        </p>
      );
    case "installed":
      return <p className={success}>{w.installed}</p>;
    case "android-app":
      return <p className={success}>{w.androidApp}</p>;
    case "ios":
      return (
        <div>
          <p className="text-sm font-medium">{w.iosTitle}</p>
          <ol className={`list-decimal ${list}`}>
            <li>{w.iosShare}</li>
            <li>{w.iosAdd}</li>
            <li>{w.iosConfirm}</li>
          </ol>
        </div>
      );
    case "manual":
      return (
        <div className="space-y-2">
          <div>
            <p className="text-sm font-medium">{w.manualTitle}</p>
            <ul className={`list-disc ${list}`}>
              <li>{w.manualAndroid}</li>
              <li>{w.manualDesktop}</li>
            </ul>
          </div>
          <p className={ui.muted}>{w.manualAlready}</p>
        </div>
      );
  }
}
